import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorCardExplicitComponent } from '@app/features/errors/components/error-card-explicit/error-card-explicit.component';
import { ErrorCardExplicitCloseableComponent } from '@app/features/errors/components/error-card-explicit-closeable/error-card-explicit-closeable.component';
import { FortigatesService } from '../../shared/fortigates.service';
import { FortigateProvisionState } from '@app/shared/models/fortigate/fortigate-provision-state.model';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-fortigate-provisioning',
    templateUrl: './fortigate-provisioning.component.html',
    styleUrls: ['./fortigate-provisioning.component.scss'],
    standalone: false
})
export class FortigateProvisioningComponent implements OnInit {

  storeNumberInput: string = "";
  storeNumberPattern: RegExp = new RegExp(/^\d{5}$/);
  isStoreNumberValid: boolean = false;
  serialNumberInput: string = "";
  serialNumberPattern: RegExp = new RegExp(/^(FGT)\d{2}(F)[a-zA-Z]{2}\d{8}$/);
  isSerialNumberValid: boolean = false;
  inferredModelNumber: string = "";
  searchDisabled: boolean = false;
  availableAdoms: any[] = [];
  selectedSystem: string = "";
  selectedAdom: string = "";
  isAdomValid: boolean = false;
  isLoading: boolean;

  ipAddressPattern: RegExp = new RegExp(/^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/);
  StateEnum = FortigateProvisionState;
  state: FortigateProvisionState = FortigateProvisionState.input;
  lanWanFieldsModel: Record<string, string> = {};

  constructor(private fortigatesService: FortigatesService, private dialog: MatDialog) { }

  ngOnInit() {
    this.isLoading = true;
    this.getAvailableAdoms().then(() => {
      this.isLoading = false;
    });
  }

  getAvailableAdoms() {
    const promise = new Promise((resolve, reject) => {
      this.fortigatesService.getAvailableAdoms().then(response => {
        response.toPromise().then(adoms_response => {
          try {
            if (adoms_response.status != 200) {
              throw Error(adoms_response.error.message || adoms_response)
            }
            this.availableAdoms = adoms_response.body.message;
          } catch (error) {
            console.log(error)
            this.dialog.open(ErrorCardExplicitComponent, { data: {
              message: "Error: failed to get available adoms.",
              detail: error
            }});
            reject(true);
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  selectAdom(value) {
    this.selectedSystem = value.split("$")[0];
    this.selectedAdom = value.split("$")[1];
    this.isAdomValid = this.selectedAdom.length > 2 && this.selectedSystem.length > 2;
  }

  async continue() {
    this.searchDisabled = true;
    this.state = this.StateEnum.loadingFields;
    await this.getComputedFields();
    this.state = this.StateEnum.review;
  }

  onStoreNumberChange(event) {
    this.isStoreNumberValid = this.storeNumberPattern.test(this.storeNumberInput);
  }

  onSerialNumberChange(event) {
    this.isSerialNumberValid = this.serialNumberPattern.test(this.serialNumberInput);
    if (this.isSerialNumberValid) {
      this.inferredModelNumber = this.serialNumberInput.substring(3, 6);
    }
  }

  private async getComputedFields() {
    const storeNumber = this.storeNumberInput;

    // LAN and WAN are pulled independently so that a failure on one side
    // (e.g. a store with no WAN source-of-truth record) still lets the other
    // side's values populate. The admin can then fill in any blanks manually.
    const [lanResult, wanResult] = await Promise.all([
      this.fetchFields(() => this.fortigatesService.getLanFields(storeNumber)),
      this.fetchFields(() => this.fortigatesService.getWanFields(storeNumber)),
    ]);

    const lanFields = lanResult.fields;
    const wanFields = wanResult.fields;
    const wanFailed = wanResult.error !== null;

    this.lanWanFieldsModel = {
      storeNumber: storeNumber,
      byodGatewayIp: lanFields["UNIT_BYOD_GW"],
      pcIp: lanFields["UNIT_PC_IP"],
      posGatewayIp: lanFields["UNIT_POS_GW"],
      // When WAN data can't be pulled, default to DHCP. DHCP hides the STATIC-only
      // fields in the form, so the tech can still proceed without manual WAN entry.
      wanType: wanFields["wan_type"] ?? (wanFailed ? "DHCP" : undefined),
      // If bandwidth is "0", set it to "-1", otherwise use the original value
      bandwidth: wanFields["bandwidth"] === "0" ? "-1" : wanFields["bandwidth"],
      gatewayIp: wanFields["gateway_ip"],
      ip: wanFields["public_ip"],
      speedDuplex: wanFields["speedduplex"],
      subnetMask: wanFields["subnet_mask"],
    }

    if (wanResult.error) {
      // 404 specifically means the WAN source-of-truth query succeeded but had no
      // entry for this store. Anything else is treated as an actual endpoint error.
      const noEntryFound = wanResult.status === 404;
      const message = noEntryFound
        ? `No WAN source-of-truth entry was found for store ${storeNumber}. WAN type has been defaulted to DHCP. If this store should be STATIC, change the type and enter the WAN values manually before provisioning.`
        : `Could not pull WAN data for store ${storeNumber} due to an endpoint error. WAN type has been defaulted to DHCP — verify this is correct and enter any STATIC values manually before provisioning.`;
      this.dialog.open(ErrorCardExplicitCloseableComponent, { data: {
        message: message,
        detail: wanResult.error
      }});
    }

    if (lanResult.error) {
      this.dialog.open(ErrorCardExplicitCloseableComponent, { data: {
        message: `Could not auto-populate LAN fields for store ${storeNumber}. Admins must enter the LAN values manually before provisioning. If you are not an admin, do not continue.`,
        detail: lanResult.error
      }});
    }
  }

  private async fetchFields(call: () => Promise<any>): Promise<{ fields: Record<string, string>, error: string | null, status: number | null }> {
    try {
      const observable = await call();
      const response: any = await firstValueFrom(observable);
      if (response.status != 200) {
        return { fields: {}, error: this.extractErrorMessage(response), status: response?.status ?? null };
      }
      return { fields: response.body?.message ?? {}, error: null, status: response.status };
    } catch (error: any) {
      console.log(error);
      return { fields: {}, error: this.extractErrorMessage(error), status: error?.status ?? null };
    }
  }

  private extractErrorMessage(response: any): string {
    return response?.error?.message
      ?? response?.body?.message
      ?? response?.message
      ?? "Unknown error";
  }

  async provisionClicked() {
    this.state = this.StateEnum.provisioning;
    const model = this.lanWanFieldsModel;
    const isStatic = model["wanType"] === "STATIC";
    const payload = {
      store_number: this.storeNumberInput,
      serial_number: this.serialNumberInput,
      system_tag: this.selectedSystem,
      adom: this.selectedAdom,
      device_type: this.inferredModelNumber,
      UNIT_Number: model["storeNumber"],
      UNIT_POS_GW: model["posGatewayIp"],
      UNIT_PC_IP: model["pcIp"],
      UNIT_BYOD_GW: model["byodGatewayIp"],
      UNIT_WAN1_TYPE: model["wanType"],
      // provision/full reads all WAN keys unconditionally; always send them.
      UNIT_WAN1_Bandwidth: isStatic ? (model["bandwidth"] ?? "") : "",
      UNIT_WAN1_GW: isStatic ? (model["gatewayIp"] ?? "") : "",
      UNIT_WAN1_IP: isStatic ? (model["ip"] ?? "") : "",
      UNIT_WAN1_Speed_Duplex: isStatic ? (model["speedDuplex"] ?? "") : "",
      UNIT_WAN1_SubnetMask: isStatic ? (model["subnetMask"] ?? "") : "",
    };

    try {
      const observable = await this.fortigatesService.provisionFull(payload);
      const response: any = await firstValueFrom(observable);
      if (response.status != 200) {
        throw Error(response.error?.message || response.body?.message || response)
      }
      this.state = this.StateEnum.finished;
    } catch (error) {
      console.log(error)
      this.dialog.open(ErrorCardExplicitComponent, { data: {
        message: "Error: provisioning failed.",
        detail: error
      }});
      this.state = this.StateEnum.review;
    }
  }

}
